import { useState, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useAllProducts } from '@/hooks/useProducts'
import { supabase } from '@/lib/supabase'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Plus,
  Package,
  Loader2,
  Edit,
  Search,
  ChevronDown,
  ArrowUpDown,
  Trash2,
} from 'lucide-react'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { getProductThumbnail } from '@/lib/product-media'
import type { Product, ProductStatus } from '@/types/database'

const statusColors: Record<ProductStatus, string> = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  featured: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  archived: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  hidden: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
}

const statusTabs: { label: string; value: string }[] = [
  { label: 'All', value: 'all' },
  { label: 'Featured', value: 'featured' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Draft', value: 'draft' },
  { label: 'Archived', value: 'archived' },
  { label: 'Hidden', value: 'hidden' },
]

type SortKey = 'newest' | 'oldest' | 'title_asc' | 'title_desc' | 'price_high' | 'price_low'

const sortOptions: { label: string; value: SortKey }[] = [
  { label: 'Newest first', value: 'newest' },
  { label: 'Oldest first', value: 'oldest' },
  { label: 'Title A-Z', value: 'title_asc' },
  { label: 'Title Z-A', value: 'title_desc' },
  { label: 'Price high-low', value: 'price_high' },
  { label: 'Price low-high', value: 'price_low' },
]

function sortProducts(products: Product[], sortKey: SortKey): Product[] {
  const sorted = [...products]
  switch (sortKey) {
    case 'newest':
      return sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    case 'oldest':
      return sorted.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    case 'title_asc':
      return sorted.sort((a, b) => a.title.localeCompare(b.title))
    case 'title_desc':
      return sorted.sort((a, b) => b.title.localeCompare(a.title))
    case 'price_high':
      return sorted.sort((a, b) => Number(b.original_price) - Number(a.original_price))
    case 'price_low':
      return sorted.sort((a, b) => Number(a.original_price) - Number(b.original_price))
    default:
      return sorted
  }
}

export function AdminProductsPage() {
  const { products, isLoading, refetch } = useAllProducts()
  const [searchParams] = useSearchParams()
  const statusFilter = searchParams.get('status') || 'all'

  const [searchQuery, setSearchQuery] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('newest')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isBulkUpdating, setIsBulkUpdating] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Status counts
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: products.length }
    for (const product of products) {
      counts[product.status] = (counts[product.status] || 0) + 1
    }
    return counts
  }, [products])

  // Filter + search + sort
  const filteredProducts = useMemo(() => {
    let result = products

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(p => p.status === statusFilter)
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(p =>
        p.title.toLowerCase().includes(q) ||
        (p.sku && p.sku.toLowerCase().includes(q)) ||
        (p.manufacturer_name && p.manufacturer_name.toLowerCase().includes(q))
      )
    }

    // Sort
    return sortProducts(result, sortKey)
  }, [products, statusFilter, searchQuery, sortKey])

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredProducts.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredProducts.map(p => p.id)))
    }
  }

  const bulkUpdateStatus = async (status: ProductStatus) => {
    if (selectedIds.size === 0) return

    setIsBulkUpdating(true)
    try {
      const { error } = await supabase
        .from('products')
        .update({ status })
        .in('id', Array.from(selectedIds))

      if (error) throw error

      toast.success(`Updated ${selectedIds.size} products to ${status}`)
      setSelectedIds(new Set())
      refetch()
    } catch (error) {
      console.error('Bulk update error:', error)
      toast.error('Failed to update products')
    } finally {
      setIsBulkUpdating(false)
    }
  }

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return

    setIsDeleting(true)
    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .in('id', Array.from(selectedIds))

      if (error) throw error

      toast.success(`Deleted ${selectedIds.size} products`)
      setSelectedIds(new Set())
      setIsDeleteDialogOpen(false)
      refetch()
    } catch (error) {
      console.error('Bulk delete error:', error)
      toast.error('Failed to delete products. Products with orders cannot be deleted.')
    } finally {
      setIsDeleting(false)
    }
  }

  const currentSortLabel = sortOptions.find(o => o.value === sortKey)?.label || 'Newest first'

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Products | Admin</title>
      </Helmet>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Products</h1>
            <p className="text-muted-foreground mt-1">
              Manage your product catalog
            </p>
          </div>
          <Link to="/admin/products/new">
            <Button className="w-full sm:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              New Product
            </Button>
          </Link>
        </div>

        {/* Status Filter Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {statusTabs.map((tab) => (
            <Link
              key={tab.value}
              to={tab.value === 'all' ? '/admin/products' : `/admin/products?status=${tab.value}`}
            >
              <Button
                variant={statusFilter === tab.value ? 'default' : 'outline'}
                size="sm"
              >
                {tab.label}
                {(statusCounts[tab.value] ?? 0) > 0 && (
                  <span className="ml-1.5 text-xs opacity-70">
                    ({statusCounts[tab.value]})
                  </span>
                )}
              </Button>
            </Link>
          ))}
        </div>

        {/* Search + Sort Row */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="pl-9"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="w-full sm:w-auto">
                <ArrowUpDown className="mr-2 size-4" />
                {currentSortLabel}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {sortOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => setSortKey(option.value)}
                  className={sortKey === option.value ? 'font-medium' : ''}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Product Count */}
        <p className="text-sm text-muted-foreground">
          {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
          {searchQuery && ` matching "${searchQuery}"`}
        </p>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="flex flex-col gap-3 rounded-lg border bg-muted/50 p-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm font-medium">
              {selectedIds.size} selected
            </span>
            <div className="flex w-full flex-wrap gap-2 sm:w-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={isBulkUpdating} className="w-full sm:w-auto">
                    {isBulkUpdating && <Loader2 className="mr-2 size-4 animate-spin" />}
                    Change Status
                    <ChevronDown className="ml-2 size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuLabel>Change Status</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => bulkUpdateStatus('draft')}>Draft</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => bulkUpdateStatus('scheduled')}>Scheduled</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => bulkUpdateStatus('archived')}>Archived</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => bulkUpdateStatus('hidden')}>Hidden</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setIsDeleteDialogOpen(true)}
                className="w-full sm:w-auto"
              >
                <Trash2 className="mr-2 size-4" />
                Delete
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())} className="w-full sm:w-auto">
                Clear
              </Button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete {selectedIds.size} product{selectedIds.size !== 1 ? 's' : ''} permanently?</DialogTitle>
              <DialogDescription>
                This action cannot be undone. Products with existing orders cannot be deleted due to foreign key constraints.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={bulkDelete} disabled={isDeleting}>
                {isDeleting && <Loader2 className="mr-2 size-4 animate-spin" />}
                Delete Permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Products Table */}
        {filteredProducts.length > 0 ? (
          <Card>
            <CardContent className="p-0">
              {/* Table Header — Desktop */}
              <div className="hidden lg:grid lg:grid-cols-[auto_1fr_100px_100px_120px_48px] items-center gap-4 border-b px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <Checkbox
                  checked={selectedIds.size === filteredProducts.length && filteredProducts.length > 0}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all"
                />
                <span>Product</span>
                <span>Status</span>
                <span className="text-right">Price</span>
                <span>SKU</span>
                <span />
              </div>

              {/* Rows */}
              <div className="divide-y">
                {filteredProducts.map((product) => (
                  <ProductRow
                    key={product.id}
                    product={product}
                    isSelected={selectedIds.has(product.id)}
                    onToggleSelect={() => toggleSelect(product.id)}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="text-center py-12">
            <Package className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-semibold">
              {searchQuery ? 'No products match your search' : 'No products yet'}
            </h3>
            <p className="text-muted-foreground mt-1">
              {searchQuery
                ? 'Try a different search term'
                : 'Create your first product to get started'}
            </p>
            {!searchQuery && (
              <Link to="/admin/products/new">
                <Button className="mt-4">
                  <Plus className="mr-2 h-4 w-4" />
                  New Product
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </>
  )
}

function ProductRow({
  product,
  isSelected,
  onToggleSelect,
}: {
  product: Product
  isSelected: boolean
  onToggleSelect: () => void
}) {
  const thumbnail = getProductThumbnail(product)

  return (
    <div className="transition-colors hover:bg-muted/50">
      {/* Desktop Row */}
      <div className="hidden lg:grid lg:grid-cols-[auto_1fr_100px_100px_120px_48px] items-center gap-4 px-4 py-3">
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggleSelect}
          aria-label={`Select ${product.title}`}
        />

        <Link to={`/admin/products/${product.id}/edit`} className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-14 rounded-md bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
            {thumbnail ? (
              <img src={thumbnail} alt={product.title} className="h-full w-full object-cover" />
            ) : (
              <Package className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-sm truncate">{product.title}</p>
            <p className="text-xs text-muted-foreground">
              {format(new Date(product.created_at), 'MMM d, yyyy')}
            </p>
          </div>
        </Link>

        <div>
          <Badge className={statusColors[product.status]}>{product.status}</Badge>
        </div>

        <p className="text-sm text-right">${Number(product.original_price).toFixed(2)}</p>

        <p className="text-xs text-muted-foreground font-mono truncate">
          {product.sku || '-'}
        </p>

        <Link to={`/admin/products/${product.id}/edit`}>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <Edit className="h-4 w-4" />
          </Button>
        </Link>
      </div>

      {/* Mobile Card */}
      <div className="lg:hidden p-4">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggleSelect}
            aria-label={`Select ${product.title}`}
            className="mt-1"
          />

          <Link to={`/admin/products/${product.id}/edit`} className="flex-1 min-w-0">
            <div className="flex items-start gap-3">
              <div className="h-16 w-24 rounded-md bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                {thumbnail ? (
                  <img src={thumbnail} alt={product.title} className="h-full w-full object-cover" />
                ) : (
                  <Package className="h-6 w-6 text-muted-foreground" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{product.title}</p>
                <p className="text-sm text-muted-foreground">
                  ${Number(product.original_price).toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {format(new Date(product.created_at), 'MMM d, yyyy')}
                </p>
                {product.sku && (
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">{product.sku}</p>
                )}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <Badge className={statusColors[product.status]}>{product.status}</Badge>
              <Button variant="ghost" size="icon" className="h-8 w-8" tabIndex={-1}>
                <Edit className="h-4 w-4" />
              </Button>
            </div>
          </Link>
        </div>
      </div>
    </div>
  )
}
